import './internal-clients.css';

import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import React, { useMemo, useState } from 'react';

import useGetRequest from '../../api/useGetRequest';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import {
  GridSearch,
  SortableHeaderCell,
  StatusDot,
} from '../../redesign-v4/components/data-grid';
import DropdownMenu from '../../redesign-v4/components/dropdown-menu';
import AgvPageHeader, {
  PageHeaderButton,
} from '../../redesign-v4/components/page-header';
import { formatDateTime } from './client-diff';
import ClientHistoryModal from './components/client-history-modal';

const VIEW = { ALL: 'ALL', WITHOUT_ADMIN: 'WITHOUT_ADMIN' };

const SORT = {
  ID: 'client_id',
  NAME: 'name',
  CITY: 'city',
  ADMINS: 'admin_users_count',
  USERS: 'users_count',
  UPDATED: 'last_configured_at',
};

const WITH_ADMIN = '#00b75f';
const WITHOUT_ADMIN = '#b7791f';

const normalize = (text) =>
  (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const compare = (a, b, field) => {
  const left = a[field];
  const right = b[field];

  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === 'number') return left - right;
  return String(left).localeCompare(String(right), 'fr');
};

const ClientLogo = ({ client }) => (
  <span className="ic-logo">
    {client.logo_url ? (
      <img src={client.logo_url} alt="" />
    ) : (
      (client.name || '?').slice(0, 2).toUpperCase()
    )}
  </span>
);

/**
 * Liste des clients (tenants) : outil interne réservé aux SUPER-ADMIN.
 * Tri et recherche côté navigateur (quelques dizaines de clients).
 */
export default function ClientsList({ onCreate, onEdit }) {
  const [query, setQuery] = useState('');
  const [view, setView] = useState(VIEW.ALL);
  const [sort, setSort] = useState({ field: SORT.ID, order: 'ASC' });
  const [historyClientId, setHistoryClientId] = useState(null);

  const { data, isLoading, isFirstLoad } = useGetRequest(
    '/super-admin/clients',
    0
  );
  const allClients = useMemo(() => data?.clients || [], [data]);
  const withoutAdminCount = allClients.filter(
    (client) => !client.admin_users_count
  ).length;

  const clients = useMemo(() => {
    const normalizedQuery = normalize(query.trim());

    return allClients
      .filter((client) => view === VIEW.ALL || !client.admin_users_count)
      .filter(
        (client) =>
          !normalizedQuery ||
          [client.name, client.city, client.siret, String(client.client_id)]
            .map(normalize)
            .some((text) => text.includes(normalizedQuery))
      )
      .sort((a, b) => {
        const result = compare(a, b, sort.field);
        return sort.order === 'ASC' ? result : -result;
      });
  }, [allClients, query, view, sort]);

  const sortProps = {
    sortField: sort.field,
    sortOrder: sort.order,
    onSort: (field, order) => setSort({ field, order }),
  };

  const renderRow = (client) => (
    <div
      key={client.client_id}
      className="agv-dg-row"
      onClick={() => onEdit(client.client_id)}
    >
      <div className="agv-dg-cell ic-cell--id">{client.client_id}</div>
      <div className="agv-dg-cell ic-cell--client">
        <ClientLogo client={client} />
        <span className="agv-dg-cell__stack">
          <span className="ic-name">{client.name}</span>
          {client.siret && (
            <span className="agv-dg-cell__sub">SIRET {client.siret}</span>
          )}
        </span>
      </div>
      <div className="agv-dg-cell ic-cell--city">
        {client.city || <span className="ic-muted">—</span>}
      </div>
      <div className="agv-dg-cell ic-cell--admins">
        {client.admin_users_count > 0 ? (
          <StatusDot
            color={WITH_ADMIN}
            label={`${client.admin_users_count} administrateur${client.admin_users_count > 1 ? 's' : ''}`}
          />
        ) : (
          <StatusDot color={WITHOUT_ADMIN} label="Aucun administrateur" />
        )}
      </div>
      <div className="agv-dg-cell ic-cell--users">{client.users_count}</div>
      <div className="agv-dg-cell ic-cell--comms">
        <span className="ic-chips">
          <span
            className={`ic-chip ${client.is_email_sending_enabled ? '' : 'ic-chip--off'}`}
            title={
              client.is_email_sending_enabled
                ? 'Envoi d’emails activé'
                : 'Envoi d’emails désactivé'
            }
          >
            Emails
          </span>
          <span
            className={`ic-chip ${client.is_sms_sending_enabled ? '' : 'ic-chip--off'}`}
            title={
              client.is_sms_sending_enabled
                ? 'Envoi de SMS activé'
                : 'Envoi de SMS désactivé'
            }
          >
            SMS
          </span>
        </span>
      </div>
      <div className="agv-dg-cell ic-cell--updated">
        {client.last_configured_at ? (
          formatDateTime(client.last_configured_at)
        ) : (
          <span className="ic-muted">Avant l’outil</span>
        )}
      </div>
      <div className="agv-dg-cell agv-dg-cell--spacer" />
      <div
        className="agv-dg-cell agv-dg-cell--more"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenu
          width={220}
          items={[
            { label: 'Paramétrer', onClick: () => onEdit(client.client_id) },
            {
              label: 'Historique du paramétrage',
              onClick: () => setHistoryClientId(client.client_id),
            },
          ]}
          renderTrigger={({ toggle }) => (
            <button
              type="button"
              className="agv-dg-icon-btn"
              onClick={toggle}
              title="Actions"
            >
              ⋯
            </button>
          )}
        />
      </div>
    </div>
  );

  const renderBody = () => {
    if (isLoading && isFirstLoad) {
      return (
        <div className="agv-dg-grid__loading">
          <GenericTableSkeleton numberOfRows={8} />
        </div>
      );
    }
    if (clients.length === 0) {
      return (
        <div className="agv-dg-empty">
          <span className="agv-dg-empty__icon">⌕</span>
          <span className="agv-dg-empty__title">Aucun client</span>
          <span className="agv-dg-empty__text">
            {query
              ? 'Aucun client ne correspond à la recherche.'
              : 'Tous les clients ont au moins un administrateur.'}
          </span>
        </div>
      );
    }
    return clients.map(renderRow);
  };

  return (
    <div className="ic-page ic-page--list">
      <AgvPageHeader
        icon={faBuilding}
        breadcrumb={['Outils internes', 'Clients']}
        title="Clients"
        count={allClients.length}
        summary="Création et paramétrage des clients Fleet (SUPER-ADMIN Akanéa)"
        activeTab={view}
        onTabChange={setView}
        tabs={[
          { key: VIEW.ALL, icon: '☰', label: 'Tous les clients' },
          {
            key: VIEW.WITHOUT_ADMIN,
            icon: '⚠',
            label: 'Sans administrateur',
            count: withoutAdminCount,
          },
        ]}
        actions={
          <PageHeaderButton primary glyph="+" onClick={onCreate}>
            Nouveau client
          </PageHeaderButton>
        }
      />

      <div className="agv-dg-grid ic-grid">
        <div className="agv-dg-toolbar">
          <div className="agv-dg-toolbar__filters">
            <GridSearch
              value={query}
              onChange={setQuery}
              placeholder="Rechercher un client, une ville, un SIRET, un ID…"
            />
          </div>
          <div className="agv-dg-toolbar__controls">
            <span className="agv-dg-pagination__range">
              {clients.length} sur {allClients.length}
            </span>
          </div>
        </div>
        <div className="agv-dg-grid__scroll">
          <div className="agv-dg-grid__table">
            <div className="agv-dg-grid__head">
              <SortableHeaderCell
                className="ic-cell--id"
                label="ID"
                field={SORT.ID}
                {...sortProps}
              />
              <SortableHeaderCell
                className="ic-cell--client"
                label="Client"
                field={SORT.NAME}
                {...sortProps}
              />
              <SortableHeaderCell
                className="ic-cell--city"
                label="Ville"
                field={SORT.CITY}
                {...sortProps}
              />
              <SortableHeaderCell
                className="ic-cell--admins"
                label="Administrateurs"
                field={SORT.ADMINS}
                {...sortProps}
              />
              <SortableHeaderCell
                className="ic-cell--users"
                label="Utilisateurs"
                field={SORT.USERS}
                {...sortProps}
              />
              <div className="agv-dg-cell ic-cell--comms">Envois</div>
              <SortableHeaderCell
                className="ic-cell--updated"
                label="Dernier paramétrage"
                field={SORT.UPDATED}
                {...sortProps}
              />
              <div className="agv-dg-cell agv-dg-cell--spacer" />
              <div className="agv-dg-cell agv-dg-cell--more" />
            </div>
            <div
              className={`agv-dg-grid__body ${isLoading && !isFirstLoad ? 'agv-dg-grid__body--loading' : ''}`}
            >
              {renderBody()}
            </div>
          </div>
        </div>
      </div>

      <ClientHistoryModal
        isOpen={historyClientId != null}
        setIsOpen={(isOpen) => !isOpen && setHistoryClientId(null)}
        clientId={historyClientId}
      />
    </div>
  );
}
